/* rk-slideshow.js v2.1 — Dot Active Fix & Optimized */
(() => {
  const SEL = {
    slider:   ".rk-slider",
    viewport: ".rk-viewport",
    slides:   ".rk-slides",
    slide:    ".rk-slide",
    dots:     ".rk-dots",
    prev:     ".rk-prev",
    next:     ".rk-next",
  };

  const parseBool = (v, def = false) => {
    if (v == null) return def;
    const s = String(v).toLowerCase().trim();
    return s === "true" || s === "1" || s === "yes";
  };

  const parseIntSafe = (v, def) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : def;
  };

  function initRkSliders(root = document) {
    root.querySelectorAll(SEL.slider).forEach((slider) => {
      if (slider.dataset.rkInited === "1") return;
      slider.dataset.rkInited = "1";

      const viewport = slider.querySelector(SEL.viewport);
      const slidesEl = slider.querySelector(SEL.slides);
      if (!viewport || !slidesEl) return;

      const originals = Array.from(slidesEl.querySelectorAll(SEL.slide))
        .filter((el) => !el.classList.contains("rk-clone"));

      const N = originals.length;
      if (N === 0) return;

      const autoplay        = parseBool(slider.dataset.autoplay, false);
      const interval        = parseIntSafe(slider.dataset.interval, 6000);
      const useArrows       = parseBool(slider.dataset.arrows, false);
      const useDots         = parseBool(slider.dataset.dots, false);
      const controlsEnabled = N > 1;

      // ── Clone (infinite loop用) ──────────────────────────────────
      // index=0 が末尾クローン、index=N+1 が先頭クローン
      if (controlsEnabled && slider.dataset.rkCloned !== "1") {
        const firstClone = originals[0].cloneNode(true);
        firstClone.classList.add("rk-clone");
        firstClone.setAttribute("aria-hidden", "true");
        firstClone.tabIndex = -1;

        const lastClone = originals[N - 1].cloneNode(true);
        lastClone.classList.add("rk-clone");
        lastClone.setAttribute("aria-hidden", "true");
        lastClone.tabIndex = -1;

        slidesEl.insertBefore(lastClone, slidesEl.firstChild);
        slidesEl.appendChild(firstClone);
        slider.dataset.rkCloned = "1";
      }

      const allSlides = Array.from(slidesEl.querySelectorAll(SEL.slide));
      const total     = allSlides.length; // クローン込み = N + 2
      // index=1 が実際の1枚目（index=0は末尾クローン）
      let index    = controlsEnabled ? 1 : 0;
      let timer    = null;
      let isPaused = false;

      // ── Pointer State ────────────────────────────────────────────
      let isPointerDown  = false;
      let startX         = 0;
      let startY         = 0;
      let startTranslate = 0;
      let dragDeltaX     = 0;
      let velocityX      = 0;
      let lastX          = 0;
      let lastTime       = 0;
      let isDragging     = false;
      let blockClick     = false;

      slider.setAttribute("tabindex", slider.getAttribute("tabindex") || "0");

      const setTransition = (on) => {
        slidesEl.style.transition = on
          ? "transform .45s cubic-bezier(0.25,0.46,0.45,0.94)"
          : "none";
      };

      const getTranslateX = () => {
        const m = getComputedStyle(slidesEl).transform;
        if (!m || m === "none") return 0;
        const isMatrix3d = m.includes("matrix3d");
        const parts = isMatrix3d
          ? m.match(/matrix3d\((.+)\)/)?.[1]?.split(",").map(Number)
          : m.match(/matrix\((.+)\)/)?.[1]?.split(",").map(Number);
        if (!parts || parts.some((x) => Number.isNaN(x))) return 0;
        return isMatrix3d ? parts[12] : parts[4];
      };

      // ── Dots ─────────────────────────────────────────────────────
      const dotsWrap   = slider.querySelector(SEL.dots);
      let   dotButtons = [];

      /**
       * BUG FIX v2.1:
       *   index はクローンを含む位置（1〜N が実スライド）。
       *   ドットの realIndex は 0〜N-1 に変換する必要がある。
       *
       *   旧: (index - 1 + N) % N  → index=1 のとき 0、index=N のとき N-1  ✓
       *   しかし初期化直後に updateDots() が呼ばれるタイミングで
       *   dotButtons がまだ空の場合があり、アクティブが反映されなかった。
       *   → moveTo() 内で updateDots() を呼ぶ順序を修正し、
       *     dotButtons 生成 → moveTo() の順番を保証する。
       */
      const updateDots = () => {
        if (!dotsWrap || !dotButtons.length) return;
        // index=1 → realIndex=0（1枚目）、index=N → realIndex=N-1（最後）
        const realIndex = (index - 1 + N) % N;
        dotButtons.forEach((b, i) => {
          b.classList.toggle("is-active", i === realIndex);
          // インラインで確実に色を制御（テーマCSSの上書き対策）
          b.style.background = i === realIndex
            ? "#000000"
            : "rgba(0,0,0,0.3)";
        });
      };

      if (useDots && dotsWrap && controlsEnabled) {
        dotsWrap.innerHTML = "";
        dotButtons = [];
        dotsWrap.style.cssText =
          "display:flex!important;justify-content:center;gap:8px;margin-top:14px;";

        for (let i = 0; i < N; i++) {
          const btn = document.createElement("button");
          btn.type      = "button";
          btn.ariaLabel = `スライド ${i + 1}`;
          btn.style.cssText = [
            "display:inline-block!important",
            "width:9px",
            "height:9px",
            "border-radius:999px",
            "border:none",
            "background:rgba(0,0,0,0.3)",
            "cursor:pointer",
            "padding:0",
            "transition:background 0.2s",
          ].join(";");
          btn.addEventListener("click", () => {
            stopAutoplay();
            moveTo(i + 1); // i=0 → index=1（1枚目）
            startAutoplay();
          });
          dotsWrap.appendChild(btn);
          dotButtons.push(btn);
        }
      }

      // ── Arrows ───────────────────────────────────────────────────
      if (useArrows && controlsEnabled) {
        let prev = slider.querySelector(SEL.prev);
        let next = slider.querySelector(SEL.next);
        if (!prev) {
          prev = document.createElement("button");
          prev.className   = "rk-prev";
          prev.type        = "button";
          prev.textContent = "‹";
          prev.setAttribute("aria-label", "前のスライド");
          slider.appendChild(prev);
        }
        if (!next) {
          next = document.createElement("button");
          next.className   = "rk-next";
          next.type        = "button";
          next.textContent = "›";
          next.setAttribute("aria-label", "次のスライド");
          slider.appendChild(next);
        }
        prev.addEventListener("click", () => {
          stopAutoplay();
          moveTo(index - 1);
          startAutoplay();
        });
        next.addEventListener("click", () => {
          stopAutoplay();
          moveTo(index + 1);
          startAutoplay();
        });
      }

      // ── Core: moveTo ─────────────────────────────────────────────
      const moveTo = (i, animate = true) => {
        // 範囲クランプ
        if (i < 0)         i = 0;
        if (i > total - 1) i = total - 1;
        index = i;
        const target = allSlides[index].offsetLeft;
        setTransition(animate);
        slidesEl.style.transform = `translateX(${-target}px)`;
        // BUG FIX v2.1: updateDots は transform 適用後に呼ぶ
        updateDots();
      };

      // 無限ループ: クローン端に到達したら本体へ瞬間移動
      slidesEl.addEventListener("transitionend", () => {
        if (!controlsEnabled) return;
        if (index === 0) {
          // 末尾クローン → 本体の末尾（index=N）へ
          index = N;
          setTransition(false);
          slidesEl.style.transform = `translateX(${-allSlides[index].offsetLeft}px)`;
          updateDots();
        } else if (index === N + 1) {
          // 先頭クローン → 本体の先頭（index=1）へ
          index = 1;
          setTransition(false);
          slidesEl.style.transform = `translateX(${-allSlides[index].offsetLeft}px)`;
          updateDots();
        }
      });

      // ── Autoplay ─────────────────────────────────────────────────
      const stopAutoplay = () => {
        if (timer) { clearInterval(timer); timer = null; }
      };
      const startAutoplay = () => {
        stopAutoplay();
        if (!autoplay || !controlsEnabled) return;
        timer = setInterval(() => {
          if (!isPaused) moveTo(index + 1);
        }, Math.max(1200, interval));
      };

      slider.addEventListener("mouseenter", () => (isPaused = true));
      slider.addEventListener("mouseleave", () => (isPaused = false));

      // ── Pointer Handlers ─────────────────────────────────────────
      const onPointerDown = (e) => {
        if (!controlsEnabled) return;
        if (e.pointerType === "mouse" && e.button !== 0) return;

        isPointerDown  = true;
        isDragging     = false;
        blockClick     = false;
        startX         = e.clientX;
        startY         = e.clientY;
        dragDeltaX     = 0;
        lastX          = e.clientX;
        lastTime       = performance.now();
        velocityX      = 0;
        startTranslate = getTranslateX();

        setTransition(false);
        stopAutoplay();

        if (e.pointerType === "mouse") {
          slider.addEventListener("dragstart", (de) => de.preventDefault(), { once: true });
        }
      };

      const onPointerMove = (e) => {
        if (!isPointerDown) return;

        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        if (!isDragging) {
          // 縦方向が優勢なら縦スクロールに譲る
          if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 10) {
            isPointerDown = false;
            startAutoplay();
            return;
          }
          // 横 5px 超でドラッグ確定
          if (Math.abs(dx) > 5) isDragging = true;
        }

        if (isDragging) {
          if (e.cancelable) e.preventDefault();

          const now = performance.now();
          const dt  = now - lastTime;
          if (dt > 0) velocityX = (e.clientX - lastX) / dt; // px/ms
          lastX      = e.clientX;
          lastTime   = now;
          dragDeltaX = dx;

          slidesEl.style.transform = `translateX(${startTranslate + dx}px)`;
        }
      };

      const onPointerUp = (e) => {
        if (!isPointerDown) return;
        isPointerDown = false;

        if (isDragging) {
          isDragging = false;
          blockClick = true;

          const dx        = dragDeltaX;
          const vx        = velocityX;
          const vwWidth   = viewport.clientWidth;
          const distThres = Math.min(80, vwWidth * 0.15);
          const velThres  = 0.3; // px/ms

          const flickRight = vx >  velThres || dx >  distThres;
          const flickLeft  = vx < -velThres || dx < -distThres;

          if      (flickRight) moveTo(index - 1);
          else if (flickLeft)  moveTo(index + 1);
          else                 moveTo(index);
        } else {
          moveTo(index);
        }

        startAutoplay();
      };

      const onPointerCancel = () => {
        if (!isPointerDown) return;
        isPointerDown = false;
        isDragging    = false;
        moveTo(index);
        startAutoplay();
      };

      // イベント登録
      viewport.addEventListener("pointerdown",   onPointerDown,   { passive: true });
      document.addEventListener("pointermove",   onPointerMove,   { passive: false });
      document.addEventListener("pointerup",     onPointerUp);
      document.addEventListener("pointercancel", onPointerCancel);

      // ドラッグ後のリンク誤発火を capture フェーズで防ぐ
      slidesEl.addEventListener("click", (e) => {
        if (blockClick) {
          e.preventDefault();
          e.stopPropagation();
          blockClick = false;
        }
      }, true);

      // ── キーボード操作 ───────────────────────────────────────────
      slider.addEventListener("keydown", (e) => {
        if (!controlsEnabled) return;
        if (e.key === "ArrowLeft") {
          stopAutoplay(); moveTo(index - 1); startAutoplay();
        }
        if (e.key === "ArrowRight") {
          stopAutoplay(); moveTo(index + 1); startAutoplay();
        }
      });

      // ── 初期化 ───────────────────────────────────────────────────
      // BUG FIX v2.1:
      //   dotButtons が生成済みの状態で moveTo() を呼ぶことで
      //   初期表示から1枚目ドットが確実に黒くなる
      moveTo(index, false);
      startAutoplay();

      // ── リサイズ対応 ─────────────────────────────────────────────
      let raf;
      window.addEventListener("resize", () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => moveTo(index, false));
      });
    });
  }

  // ── Shopify イベント対応 ────────────────────────────────────────
  document.addEventListener("DOMContentLoaded",        () => initRkSliders());
  document.addEventListener("shopify:section:load",    (e) => initRkSliders(e.target));
  document.addEventListener("shopify:section:reorder", ()  => initRkSliders());
})();